import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Chip,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Add, Clear, Search, ViewList, ViewModule, UploadFile, Merge } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { authGet, authGetBlob, authPost, authPatch, authDelete } from '../../lib/api';
import SavedColumnViewControls, { useSavedColumnView } from '../../components/SavedColumnViewControls';
import { formatDate } from '../../lib/dateFormat';
import { referralSourceOptionsFor } from '../../lib/referralSources';
import { classSessionLabel } from './studentEnrollmentDisplay';

const EXIT_INTERVIEW_PROGRAM_OPTIONS = [
  { value: '', label: 'Not selected' },
  { value: 'dwi_education', label: 'Texas DWI Education Program' },
  { value: 'dwi_intervention', label: 'DWI Intervention Program' },
  { value: 'doep', label: 'Drug Offender Education Program' }
];

const STUDENT_MERGE_FIELDS = [
  ['code', 'Code'],
  ['ssn', 'SSN Last 4'],
  ['last_name', 'Last Name'],
  ['first_name', 'First Name'],
  ['middle_name', 'Middle Name'],
  ['birth_date', 'Birth Date'],
  ['address', 'Address'],
  ['city', 'City'],
  ['state', 'State'],
  ['zip', 'ZIP'],
  ['phone', 'Phone'],
  ['email', 'Email'],
  ['driver_number', 'Driver Number'],
  ['cause_number', 'Cause Number'],
  ['county_of_conviction', 'County of Conviction'],
  ['probation_officer_name', 'Probation Officer'],
  ['probation_officer_email', 'Probation Email'],
  ['probation_officer_phone', 'Probation Phone'],
  ['attorney_name', 'Attorney'],
  ['attorney_email', 'Attorney Email'],
  ['attorney_phone', 'Attorney Phone'],
  ['referral_source', 'Referral Source'],
  ['state_reporting_notes', 'Additional State Reporting Fields'],
  ['reporting_agency', 'Reporting Agency'],
  ['call_notes', 'Call Notes']
];

function formatMergeValue(student, field) {
  if (!student) return '';
  if (field === 'ssn') return maskSSN(student.ssn, student.ssn_last4);
  if (field.includes('_date') || field === 'birth_date') return student[field] ? String(student[field]).slice(0, 10) : '';
  return student[field] || '';
}

function studentMergeLabel(student = {}) {
  const name = `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.replace(/\s+/g, ' ').trim() || 'Unnamed student';
  const details = [student.code, formatMergeValue(student, 'ssn') && `SSN ${formatMergeValue(student, 'ssn')}`, student.birth_date ? formatDate(student.birth_date) : '', student.phone].filter(Boolean).join(' • ');
  return details ? `${name} — ${details}` : name;
}



function blankEvaluationOption(program = '') {
  return { program, result: '', narrative: '' };
}

function ContactPairTable({ student }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>Probation name</Typography>
          <Typography variant="body2">{student?.probation_officer_name || ''}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>Attorney Name</Typography>
          <Typography variant="body2">{student?.attorney_name || ''}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>Probation Ph</Typography>
          <Typography variant="body2">{formatPhone(student?.probation_officer_phone) || ''}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>Attorney Ph</Typography>
          <Typography variant="body2">{formatPhone(student?.attorney_phone) || ''}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>Probation Email</Typography>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{student?.probation_officer_email || ''}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>Attorney Email</Typography>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{student?.attorney_email || ''}</Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}

function openPdfBlob(blob) {
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'exit-interview.pdf';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

function formatPhone(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatSSNLast4(value) {
  return String(value || '').replace(/\D/g, '').slice(-4);
}

function maskSSN(value, last4 = '') {
  return formatSSNLast4(value || last4);
}

function formatTimeLabel(time24) {
  if (!time24) return '';
  const [hourStr, minute] = String(time24).slice(0, 5).split(':');
  const hour = Number(hourStr);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${suffix}`;
}

function normalizeSearch(value) {
  return String(value || '').toLowerCase();
}

function normalizeDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function studentMatchesSearch(student, query) {
  const textQuery = normalizeSearch(query).trim();
  if (!textQuery) return true;

  const searchableValues = [
    student.code,
    student.ssn,
    student.ssn_last4,
    student.last_name,
    student.first_name,
    student.middle_name,
    `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`,
    `${student.first_name || ''} ${student.last_name || ''}`,
    student.birth_date,
    student.address,
    student.city,
    student.state,
    student.zip,
    student.phone,
    student.email,
    student.call_notes,
    student.driver_number,
    student.cause_number,
    student.county_of_conviction,
    student.probation_officer_name,
    student.probation_officer_email,
    student.probation_officer_phone,
    student.attorney_name,
    student.attorney_email,
    student.attorney_phone,
    student.reporting_agency,
    student.referral_source,
    student.state_reporting_notes
  ];
  const textValues = searchableValues.map(normalizeSearch);
  const digitValues = searchableValues.map(normalizeDigits);
  const tokens = textQuery.split(/\s+/).filter(Boolean);

  return tokens.every((token) => {
    const tokenText = normalizeSearch(token);
    const tokenDigits = normalizeDigits(token);
    const textMatch = textValues.some((value) => value.includes(tokenText));
    if (!tokenDigits) return textMatch;
    return textMatch || digitValues.some((value) => value.includes(tokenDigits));
  });
}
export default function StudentsPage() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [studentSearch, setStudentSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [mergeFieldSources, setMergeFieldSources] = useState({});
  const [mergeError, setMergeError] = useState('');

  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentHistory, setStudentHistory] = useState([]);
  const [searchError, setSearchError] = useState('');
  const [evaluationOptions, setEvaluationOptions] = useState([]);
  const [evaluationManagerOpen, setEvaluationManagerOpen] = useState(false);
  const [evaluationOptionForm, setEvaluationOptionForm] = useState(blankEvaluationOption());
  const [evaluationOptionEditing, setEvaluationOptionEditing] = useState(null);
  const [evaluationOptionError, setEvaluationOptionError] = useState('');

  const formRef = useRef(null);

  const [form, setForm] = useState({
    code: '',
    ssn: '',
    last_name: '',
    first_name: '',
    middle_name: '',
    birth_date: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    call_notes: '',
    driver_number: '',

    cause_number: '',
    county_of_conviction: '',
    probation_officer_name: '',
    probation_officer_email: '',
    probation_officer_phone: '',
    attorney_name: '',
    attorney_email: '',
    attorney_phone: '',
    reporting_agency: '',
    referral_source: '',
    state_reporting_notes: ''
  });

  const loadStudents = useCallback(async (searchValue = '') => {
    setLoading(true);
    setSearchError('');
    try {
      const params = new URLSearchParams();
      if (searchValue) params.set('q', searchValue);
      if (searchValue) params.set('limit', '1000');
      const data = await authGet(`/api/students?${params.toString()}`);
      const nextStudents = data.students || [];
      setStudents(nextStudents);
      return nextStudents;
    } catch (err) {
      console.error('Load students error:', err);
      setSearchError(err.message || 'Unable to search students');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEvaluationOptions = useCallback(async () => {
    try {
      const data = await authGet('/api/students/evaluation-results/options');
      setEvaluationOptions(data.options || []);
    } catch (err) {
      console.error('Load evaluation result options error:', err);
    }
  }, []);

  const resetEvaluationOptionForm = (program = '') => {
    setEvaluationOptionForm(blankEvaluationOption(program));
    setEvaluationOptionEditing(null);
    setEvaluationOptionError('');
  };

  const handleSaveEvaluationOption = async () => {
    try {
      const payload = {
        program: evaluationOptionForm.program || '',
        result: evaluationOptionForm.result.trim(),
        narrative: evaluationOptionForm.narrative.trim()
      };
      if (!payload.result) {
        setEvaluationOptionError('Evaluation result is required.');
        return;
      }
      if (evaluationOptionEditing) {
        await authPatch(`/api/students/evaluation-results/options/${evaluationOptionEditing.id}`, payload);
      } else {
        await authPost('/api/students/evaluation-results/options', payload);
      }
      await loadEvaluationOptions();
      resetEvaluationOptionForm(payload.program);
    } catch (err) {
      console.error('Save evaluation result option error:', err);
      setEvaluationOptionError(err.message || 'Unable to save evaluation result option.');
    }
  };

  const handleEditEvaluationOption = (option) => {
    setEvaluationOptionEditing(option);
    setEvaluationOptionForm({ program: option.program || '', result: option.result || '', narrative: option.narrative || '' });
    setEvaluationOptionError('');
  };

  const handleDeleteEvaluationOption = async (option) => {
    if (!window.confirm(`Delete evaluation result “${option.result}”? Existing student records will keep their saved text.`)) return;
    try {
      await authDelete(`/api/students/evaluation-results/options/${option.id}`);
      await loadEvaluationOptions();
      if (evaluationOptionEditing?.id === option.id) resetEvaluationOptionForm(option.program || '');
    } catch (err) {
      console.error('Delete evaluation result option error:', err);
      setEvaluationOptionError(err.message || 'Unable to delete evaluation result option.');
    }
  };

  const handleSearchSubmit = async (event) => {
    event?.preventDefault();
    const nextSearch = studentSearch.trim();
    setActiveSearch(nextSearch);
    await loadStudents(nextSearch);
  };

  const clearSearch = async () => {
    setStudentSearch('');
    setActiveSearch('');
    await loadStudents('');
  };

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    loadEvaluationOptions();
  }, [loadEvaluationOptions]);

  useEffect(() => {
    const nextSearch = studentSearch.trim();
    const handle = setTimeout(() => {
      setActiveSearch(nextSearch);
      loadStudents(nextSearch);
    }, nextSearch ? 300 : 0);
    return () => clearTimeout(handle);
  }, [studentSearch, loadStudents]);

  const resetForm = () => {
    setForm({
      code: '',
      ssn: '',
      last_name: '',
      first_name: '',
      middle_name: '',
      birth_date: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      call_notes: '',
      driver_number: '',
      cause_number: '',
      county_of_conviction: '',
      probation_officer_name: '',
      probation_officer_email: '',
      probation_officer_phone: '',
      attorney_name: '',
      attorney_email: '',
      attorney_phone: '',
      reporting_agency: '',
      referral_source: '',
      state_reporting_notes: ''
    });
    setEditing(null);
  };

  const handleSave = async (event) => {
    event?.preventDefault();

    try {
      const formData = formRef.current ? new FormData(formRef.current) : null;
      const fieldValue = (field) => formData?.get(field) ?? form[field] ?? '';
      const payload = {
        code: fieldValue('code'),
        ssn: formatSSNLast4(fieldValue('ssn')),
        last_name: fieldValue('last_name'),
        first_name: fieldValue('first_name'),
        middle_name: fieldValue('middle_name'),
        birth_date: fieldValue('birth_date') || null,
        address: fieldValue('address'),
        city: fieldValue('city'),
        state: fieldValue('state'),
        zip: fieldValue('zip'),
        phone: formatPhone(fieldValue('phone')),
        email: fieldValue('email'),
        call_notes: fieldValue('call_notes'),
        driver_number: fieldValue('driver_number'),
        cause_number: fieldValue('cause_number'),
        county_of_conviction: fieldValue('county_of_conviction'),
        probation_officer_name: fieldValue('probation_officer_name'),
        probation_officer_email: fieldValue('probation_officer_email'),
        probation_officer_phone: formatPhone(fieldValue('probation_officer_phone')),
        attorney_name: fieldValue('attorney_name'),
        attorney_email: fieldValue('attorney_email'),
        attorney_phone: formatPhone(fieldValue('attorney_phone')),
        reporting_agency: fieldValue('reporting_agency'),
        referral_source: fieldValue('referral_source'),
        state_reporting_notes: fieldValue('state_reporting_notes')
      };

      if (editing) {
        await authPatch(`/api/students/${editing.id}`, payload);
      } else {
        await authPost('/api/students', payload);
      }

      setOpen(false);
      resetForm();
      await loadStudents(activeSearch);
    } catch (err) {
      console.error('Save student error:', err);
      alert(err.message || 'Unable to save student. Please check the student fields and try again.');
    }
  };

  const handleEdit = (row) => {
    setDetailOpen(false);
    setEditing(row);
    setForm({
      code: row.code || '',
      ssn: formatSSNLast4(row.ssn || row.ssn_last4 || ''),
      last_name: row.last_name || '',
      first_name: row.first_name || '',
      middle_name: row.middle_name || '',
      birth_date: row.birth_date ? String(row.birth_date).slice(0, 10) : '',
      address: row.address || '',
      city: row.city || '',
      state: row.state || '',
      zip: row.zip || '',
      phone: row.phone || '',
      email: row.email || '',
      call_notes: row.call_notes || '',
      driver_number: row.driver_number || '',
      cause_number: row.cause_number || '',
      county_of_conviction: row.county_of_conviction || '',
      probation_officer_name: row.probation_officer_name || '',
      probation_officer_email: row.probation_officer_email || '',
      probation_officer_phone: row.probation_officer_phone || '',
      attorney_name: row.attorney_name || '',
      attorney_email: row.attorney_email || '',
      attorney_phone: row.attorney_phone || '',
      reporting_agency: row.reporting_agency || '',
      referral_source: row.referral_source || '',
      state_reporting_notes: row.state_reporting_notes || ''
    });
    setOpen(true);
  };

  const handleView = async (row) => {
    setSelectedStudent(row);
    setStudentHistory([]);
    setDetailOpen(true);

    try {
      const data = await authGet(`/api/students/${row.id}`);
      const student = data.student || row;
      setSelectedStudent(student);
      setStudentHistory(data.history || []);
    } catch (err) {
      console.error('Load student detail error:', err);
      alert(err.message || 'Unable to load student details.');
    }
  };

  const handleDelete = async (row) => {
    const id = row?.id || row;
    if (!window.confirm('Delete this student?')) return;

    try {
      await authDelete(`/api/students/${id}`);
      await loadStudents();
    } catch (err) {
      console.error('Delete student error:', err);
    }
  };

  const handlePrintExitInterview = async (interview) => {
    try {
      const blob = await authGetBlob(`/api/rosters/${interview.id}/students/${selectedStudent.id}/exit-interview.pdf`);
      openPdfBlob(blob);
    } catch (err) {
      console.error('Print exit interview error:', err);
      alert(err.message || 'Unable to generate the exit interview PDF.');
    }
  };

  const handleImportPlaceholder = () => {
    alert('Student import workflow will be added here.');
  };

  const rows = useMemo(
    () =>
      students
        .filter((student) => studentMatchesSearch(student, activeSearch))
        .map((student) => ({
          ...student,
          id: student.id,
          full_name: `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.replace(/\s+/g, ' ').trim(),
          birth_date_display: formatDate(student.birth_date),
          cause_number_display: student.cause_number || '',
          address_display: [student.address, student.city, student.state, student.zip].filter(Boolean).join(', '),
          phone_display: formatPhone(student.phone),
          ssn_masked: maskSSN(student.ssn, student.ssn_last4)
        })),
    [students, activeSearch]
  );

  const selectedMergeStudents = useMemo(
    () => selectedStudentIds.map((id) => rows.find((student) => student.id === id)).filter(Boolean),
    [selectedStudentIds, rows]
  );

  const openMergeStudentsDialog = () => {
    if (selectedMergeStudents.length < 2) {
      setSearchError('Select at least two students to merge.');
      return;
    }
    const firstId = selectedMergeStudents[0]?.id || '';
    const initialSources = {};
    STUDENT_MERGE_FIELDS.forEach(([field]) => {
      const studentWithValue = selectedMergeStudents.find((student) => formatMergeValue(student, field));
      initialSources[field] = studentWithValue?.id || firstId;
    });
    setMergeTargetId(firstId);
    setMergeFieldSources(initialSources);
    setMergeError('');
    setMergeOpen(true);
  };

  const handleMergeStudents = async () => {
    const targetStudent = selectedMergeStudents.find((student) => student.id === mergeTargetId);
    if (!targetStudent) {
      setMergeError('Choose the student record to keep.');
      return;
    }
    const fields = {};
    STUDENT_MERGE_FIELDS.forEach(([field]) => {
      const source = selectedMergeStudents.find((student) => student.id === (mergeFieldSources[field] || mergeTargetId)) || targetStudent;
      fields[field] = field === 'ssn' ? formatSSNLast4(source.ssn || source.ssn_last4 || '') : (source[field] ?? '');
    });

    try {
      await authPost('/api/students/merge', {
        target_student_id: mergeTargetId,
        source_student_ids: selectedMergeStudents.filter((student) => student.id !== mergeTargetId).map((student) => student.id),
        fields
      });
      setMergeOpen(false);
      setSelectedStudentIds([]);
      await loadStudents(activeSearch);
    } catch (err) {
      console.error('Merge students error:', err);
      setMergeError(err.message || 'Unable to merge students.');
    }
  };

  const columns = [
    { field: 'code', headerName: 'Code', width: 110 },
    { field: 'full_name', headerName: 'Student Name', minWidth: 210, flex: 1.3 },
    { field: 'ssn_masked', headerName: 'SSN Last 4', width: 120 },
    { field: 'birth_date_display', headerName: 'Birth Date', width: 120 },
    { field: 'phone_display', headerName: 'Phone', width: 145 },
    { field: 'driver_number', headerName: 'Driver Number', width: 150 },
    { field: 'cause_number_display', headerName: 'Cause #', width: 160 },
    { field: 'county_of_conviction', headerName: 'County of Conviction', width: 180 },
    { field: 'probation_officer_name', headerName: 'Probation Officer', width: 180 },
    { field: 'probation_officer_email', headerName: 'Probation Email', width: 200 },
    { field: 'probation_officer_phone', headerName: 'Probation Phone', width: 160 },
    { field: 'attorney_name', headerName: 'Attorney', width: 160 },
    { field: 'attorney_email', headerName: 'Attorney Email', width: 200 },
    { field: 'attorney_phone', headerName: 'Attorney Phone', width: 160 },
    { field: 'referral_source', headerName: 'Referral Source', width: 170 },
    { field: 'address_display', headerName: 'Address / City / State / ZIP', minWidth: 260, flex: 1.4 },
    { field: 'email', headerName: 'Email', minWidth: 190, flex: 1 },
    { field: 'reporting_agency', headerName: 'Reporting Agency', width: 180 },
    { field: 'call_notes', headerName: 'Call Notes', minWidth: 180, flex: 1 },

    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" gap={1}>
          <Button size="small" variant="contained" onClick={() => handleView(params.row)}>
            View
          </Button>

          <Button size="small" color="error" variant="contained" onClick={() => handleDelete(params.row)}>
            Delete
          </Button>
        </Box>
      )
    }
  ];

  const {
    columnVisibilityModel,
    setColumnVisibilityModel,
    setFieldVisible,
    showSimpleView,
    showFullView,
    resetView
  } = useSavedColumnView('students', columns, ['code', 'full_name', 'ssn_masked', 'birth_date_display', 'phone_display', 'cause_number_display']);

  return (
    <Box p={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h3" gutterBottom>
            Students
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage student records and class history.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(_, value) => value && setViewMode(value)}>
            <ToggleButton value="list">
              <ViewList fontSize="small" />
            </ToggleButton>
            <ToggleButton value="cards">
              <ViewModule fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Button variant="outlined" startIcon={<UploadFile />} onClick={handleImportPlaceholder}>
            Import Students
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            startIcon={<Merge />}
            onClick={openMergeStudentsDialog}
            disabled={selectedStudentIds.length < 2}
          >
            Merge Selected ({selectedStudentIds.length})
          </Button>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            Add Student
          </Button>
        </Stack>
      </Stack>

      <Paper component="form" onSubmit={handleSearchSubmit} sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            label="Search any student detail"
            placeholder="Name, email, phone, SSN last 4, case #, address, officer, attorney, notes, or Exit Interview text"
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
          />
          <Button type="submit" variant="contained" startIcon={<Search />} sx={{ minWidth: 120 }}>
            Search
          </Button>
          <Button variant="outlined" startIcon={<Clear />} onClick={clearSearch} disabled={!studentSearch && !activeSearch} sx={{ minWidth: 100 }}>
            Clear
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Search still checks all student fields. Multiple words now narrow results instead of fighting the search. Example: last name + DOB, phone + first name, or case number + attorney.
        </Typography>
        {activeSearch && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing {rows.length} result{rows.length === 1 ? '' : 's'} for “{activeSearch}”.
          </Typography>
        )}
      </Paper>

      {searchError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSearchError('')}>
          {searchError}
        </Alert>
      )}

      {viewMode === 'list' ? (
        <Paper sx={{ p: 2 }}>
          <SavedColumnViewControls
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            setFieldVisible={setFieldVisible}
            showSimpleView={showSimpleView}
            showFullView={showFullView}
            resetView={resetView}
          />
          <Box sx={{ height: 620, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={58}
              checkboxSelection
              rowSelectionModel={selectedStudentIds}
              onRowSelectionModelChange={(selection) => setSelectedStudentIds(Array.from(selection))}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
            />
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {rows.map((student) => (
            <Grid item xs={12} md={6} lg={4} key={student.id}>
              <Paper sx={{ p: 3 }}>
                <Stack spacing={0.75}>
                  <FormControlLabel
                    control={<Checkbox checked={selectedStudentIds.includes(student.id)} onChange={(e) => setSelectedStudentIds((prev) => e.target.checked ? Array.from(new Set([...prev, student.id])) : prev.filter((id) => id !== student.id))} />}
                    label="Select for merge"
                  />
                  <Typography variant="h6">
                    {student.first_name} {student.middle_name || ''} {student.last_name}
                  </Typography>
                  <Typography variant="body2"><strong>Code:</strong> {student.code || '—'}</Typography>
                  <Typography variant="body2"><strong>SSN Last 4:</strong> {student.ssn_masked || '—'}</Typography>
                  <Typography variant="body2"><strong>Birth:</strong> {formatDate(student.birth_date, '—')}</Typography>
                  <Typography variant="body2"><strong>Phone:</strong> {student.phone_display || '—'}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {student.email || '—'}</Typography>
                  <Typography variant="body2"><strong>Address:</strong> {student.address || '—'}</Typography>
                  <Typography variant="body2"><strong>City/State/ZIP:</strong> {[student.city, student.state, student.zip].filter(Boolean).join(', ') || '—'}</Typography>
                  <Typography variant="body2"><strong>Driver Number:</strong> {student.driver_number || '—'}</Typography>
                  <Typography variant="body2"><strong>Cause #:</strong> {student.cause_number || '—'}</Typography>
                  <Typography variant="body2"><strong>County of Conviction:</strong> {student.county_of_conviction || '—'}</Typography>
                  <ContactPairTable student={student} />
                  <Typography variant="body2"><strong>Referral Source:</strong> {student.referral_source || '—'}</Typography>
                  <Typography variant="body2"><strong>Reporting Agency:</strong> {student.reporting_agency || '—'}</Typography>
                  <Typography variant="body2"><strong>Call Notes:</strong> {student.call_notes || '—'}</Typography>

                  <Stack direction="row" spacing={1} mt={1}>
                    <Button size="small" variant="contained" onClick={() => handleView(student)}>
                      View
                    </Button>

                    <Button size="small" color="error" variant="contained" onClick={() => handleDelete(student)}>
                      Delete
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle>{editing ? 'Edit Student' : 'Add Student'}</DialogTitle>
        <Box component="form" id="student-profile-form" ref={formRef} onSubmit={handleSave}>
          <DialogContent dividers sx={{ pb: 2 }}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth name="code" label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                name="ssn"
                label="SSN Last 4"
                value={form.ssn}
                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                helperText="Enter last four digits only."
                onChange={(e) => setForm({ ...form, ssn: formatSSNLast4(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth name="driver_number" label="Driver Number" value={form.driver_number} onChange={(e) => setForm({ ...form, driver_number: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth name="birth_date" label="Birth Date" type="date" InputLabelProps={{ shrink: true }} value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth name="first_name" label="First Name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth name="middle_name" label="Middle Name" value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth name="last_name" label="Last Name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth name="address" label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth name="city" label="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={1.5}>
              <TextField fullWidth name="state" label="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={1.5}>
              <TextField fullWidth name="zip" label="ZIP" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth name="phone" label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="email" label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>


            <Grid item xs={12}>
              <Divider textAlign="left">Court / Reporting</Divider>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth name="cause_number" label="Cause Number" value={form.cause_number} onChange={(e) => setForm({ ...form, cause_number: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth name="county_of_conviction" label="County of Conviction" value={form.county_of_conviction} onChange={(e) => setForm({ ...form, county_of_conviction: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="probation_officer_name" label="Probation name" value={form.probation_officer_name} onChange={(e) => setForm({ ...form, probation_officer_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="attorney_name" label="Attorney Name" value={form.attorney_name} onChange={(e) => setForm({ ...form, attorney_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="probation_officer_phone" label="Probation Ph" value={form.probation_officer_phone} onChange={(e) => setForm({ ...form, probation_officer_phone: formatPhone(e.target.value) })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="attorney_phone" label="Attorney Ph" value={form.attorney_phone} onChange={(e) => setForm({ ...form, attorney_phone: formatPhone(e.target.value) })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="probation_officer_email" label="Probation Email" value={form.probation_officer_email} onChange={(e) => setForm({ ...form, probation_officer_email: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth name="attorney_email" label="Attorney Email" value={form.attorney_email} onChange={(e) => setForm({ ...form, attorney_email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth name="referral_source" label="Referral Source" value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })}>
                <MenuItem value="">Not selected</MenuItem>
                {referralSourceOptionsFor(form.referral_source).map((source) => (
                  <MenuItem key={source} value={source}>{source}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} name="state_reporting_notes" label="Additional State Reporting Fields" value={form.state_reporting_notes} onChange={(e) => setForm({ ...form, state_reporting_notes: e.target.value })} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth name="reporting_agency" label="Reporting Agency" value={form.reporting_agency} onChange={(e) => setForm({ ...form, reporting_agency: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} name="call_notes" label="Call Notes" value={form.call_notes} onChange={(e) => setForm({ ...form, call_notes: e.target.value })} />
            </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', zIndex: 1 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" type="submit">
              Save Student
            </Button>
          </DialogActions>
        </Box>
      </Dialog>


      <Dialog open={mergeOpen} onClose={() => setMergeOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Merge Students</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {mergeError && <Alert severity="error" onClose={() => setMergeError('')}>{mergeError}</Alert>}
            <Alert severity="warning">
              Merge will keep one student active, move class history, roster records, attendance, payments, and recents to that student, then mark the duplicates inactive.
            </Alert>
            <TextField select fullWidth label="Student record to keep" value={mergeTargetId} onChange={(e) => setMergeTargetId(e.target.value)}>
              {selectedMergeStudents.map((student) => (
                <MenuItem key={student.id} value={student.id}>{studentMergeLabel(student)}</MenuItem>
              ))}
            </TextField>
            <Typography variant="subtitle2">Choose which record's value to keep for each field</Typography>
            <Grid container spacing={1.5}>
              {STUDENT_MERGE_FIELDS.map(([field, label]) => {
                const sourceId = mergeFieldSources[field] || mergeTargetId;
                const selectedSource = selectedMergeStudents.find((student) => student.id === sourceId) || selectedMergeStudents[0];
                return (
                  <Grid item xs={12} md={6} key={field}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={label}
                      value={sourceId}
                      helperText={`Keeping: ${formatMergeValue(selectedSource, field) || 'blank'}`}
                      onChange={(e) => setMergeFieldSources((prev) => ({ ...prev, [field]: e.target.value }))}
                    >
                      {selectedMergeStudents.map((student) => (
                        <MenuItem key={student.id} value={student.id}>
                          {studentMergeLabel(student)} — {formatMergeValue(student, field) || 'blank'}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeOpen(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={handleMergeStudents} disabled={selectedMergeStudents.length < 2 || !mergeTargetId}>
            Merge Students
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={evaluationManagerOpen} onClose={() => setEvaluationManagerOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Manage Evaluation Results Indicates</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {evaluationOptionError && <Alert severity="error" onClose={() => setEvaluationOptionError('')}>{evaluationOptionError}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField select fullWidth label="Program" value={evaluationOptionForm.program} onChange={(e) => setEvaluationOptionForm({ ...evaluationOptionForm, program: e.target.value })}>
                  {EXIT_INTERVIEW_PROGRAM_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'none'} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="Evaluation Result" value={evaluationOptionForm.result} onChange={(e) => setEvaluationOptionForm({ ...evaluationOptionForm, result: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} label="Default Narrative / Explanation" value={evaluationOptionForm.narrative} onChange={(e) => setEvaluationOptionForm({ ...evaluationOptionForm, narrative: e.target.value })} />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={handleSaveEvaluationOption}>{evaluationOptionEditing ? 'Save Option' : 'Add Option'}</Button>
              {evaluationOptionEditing && <Button variant="outlined" onClick={() => resetEvaluationOptionForm(evaluationOptionForm.program)}>Cancel Edit</Button>}
            </Stack>
            <Divider />
            <Stack spacing={1}>
              {evaluationOptions.map((option) => (
                <Paper key={option.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2">{option.result}</Typography>
                      <Typography variant="caption" color="text.secondary">{EXIT_INTERVIEW_PROGRAM_OPTIONS.find((item) => item.value === option.program)?.label || 'No program selected'}</Typography>
                      {option.narrative && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{option.narrative}</Typography>}
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={() => handleEditEvaluationOption(option)}>Edit</Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteEvaluationOption(option)}>Delete</Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvaluationManagerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Student Details</DialogTitle>
        <DialogContent>
          {selectedStudent && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">
                  {selectedStudent.first_name} {selectedStudent.middle_name || ''} {selectedStudent.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Code: {selectedStudent.code || '—'} • Driver #: {selectedStudent.driver_number || '—'}
                </Typography>
              </Box>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2"><strong>SSN Last 4:</strong> {formatSSNLast4(selectedStudent.ssn || selectedStudent.ssn_last4) || '—'}</Typography>
                  <Typography variant="body2"><strong>Birth Date:</strong> {formatDate(selectedStudent.birth_date, '—')}</Typography>
                  <Typography variant="body2"><strong>Phone:</strong> {formatPhone(selectedStudent.phone) || '—'}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {selectedStudent.email || '—'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2"><strong>Address:</strong> {selectedStudent.address || '—'}</Typography>
                  <Typography variant="body2"><strong>City:</strong> {selectedStudent.city || '—'}</Typography>
                  <Typography variant="body2"><strong>State / ZIP:</strong> {[selectedStudent.state, selectedStudent.zip].filter(Boolean).join(' / ') || '—'}</Typography>
                </Grid>
              </Grid>

              <Divider />

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6">Court / Reporting</Typography>
                  <Button size="small" variant="outlined" onClick={() => selectedStudent && handleEdit(selectedStudent)}>Edit Student Fields</Button>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2"><strong>Cause #:</strong> {selectedStudent.cause_number || '—'}</Typography>
                    <Typography variant="body2"><strong>County of Conviction:</strong> {selectedStudent.county_of_conviction || '—'}</Typography>

                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ContactPairTable student={selectedStudent} />
                    <Typography variant="body2" sx={{ mt: 1 }}><strong>State Reporting Notes:</strong> {selectedStudent.state_reporting_notes || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>Referral Source:</strong> {selectedStudent.referral_source || '—'}</Typography>
                    <Typography variant="body2"><strong>Reporting Agency:</strong> {selectedStudent.reporting_agency || '—'}</Typography>
                  </Grid>
                </Grid>
              </Box>


              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>Exit Interviews by Class</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Exit Interviews are maintained from the class roster. This view is read-only and shows only records saved for the matching scheduled class.
                </Typography>
                {studentHistory.filter((item) => item.exit_interview_id).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No class-scoped Exit Interviews found.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {studentHistory.filter((item) => item.exit_interview_id).map((item) => (
                      <Paper key={item.exit_interview_id} variant="outlined" sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
                          <Box>
                            <Typography variant="subtitle2">
                              {item.class_code ? `${item.class_code} - ` : ''}{item.class_name}
                              {classSessionLabel(item.class_time) ? ` • ${classSessionLabel(item.class_time)}` : ''}
                            </Typography>
                            <Typography variant="body2">Program: {item.exit_interview_program || '—'} · Completion: {formatDate(item.exit_interview_completion_date, '—')}</Typography>
                            <Typography variant="body2">Rating: {item.exit_interview_participation_rating || '—'} · Instructor: {item.exit_interview_instructor || item.instructor || '—'}</Typography>
                          </Box>
                          <Button size="small" variant="outlined" onClick={() => handlePrintExitInterview(item)}>Export exact PDF</Button>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Classes Taken
                </Typography>

                {studentHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No class history found for this student.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {studentHistory.map((item) => (
                      <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {item.class_code ? `${item.class_code} - ` : ''}{item.class_name}
                                {classSessionLabel(item.class_time) ? ` • ${classSessionLabel(item.class_time)}` : ''}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(item.completed_date, '—')} {formatTimeLabel(item.class_time)}
                                {' '}to {formatDate(item.end_date, '—')} {formatTimeLabel(item.end_time)}
                                {' '}• Instructor: {item.instructor || '—'}
                              </Typography>
                              {item.location && (
                                <Typography variant="body2" color="text.secondary">
                                  Location: {item.location}
                                </Typography>
                              )}
                            </Box>

                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <Chip label={`DAST: ${item.dast_code || '—'}`} size="small" />
                              <Chip label={`Pre: ${item.pre_test_score ?? '—'}`} size="small" />
                              <Chip label={`Post: ${item.post_test_score ?? '—'}`} size="small" />
                              <Chip label={`TX Cert: ${item.tx_cert_number || '—'}`} size="small" />
                              <Chip label={`Due: $${Number(item.amount_due || 0).toFixed(2)}`} size="small" />
                            </Stack>
                          </Stack>

                          {Array.isArray(item.attendance) && item.attendance.length > 0 && (
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {item.attendance.map((a) => (
                                <Chip
                                  key={`${item.id}-${a.attendance_date}`}
                                  size="small"
                                  color={a.attended ? 'success' : 'default'}
                                  label={`${formatDate(a.attendance_date)}: ${a.attended ? 'Present' : 'Absent'}`}
                                />
                              ))}
                            </Stack>
                          )}

                          {item.cause_number && (
                            <Typography variant="body2">
                              <strong>Cause #:</strong> {item.cause_number}
                            </Typography>
                          )}

                          {item.notes && (
                            <Typography variant="body2">
                              <strong>Notes:</strong> {item.notes}
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}